/**
 * StoreFinder — client-side store locator (theme section, not an app extension).
 *
 * Data: static JSON asset (assets/store-finder-data.json, or an external URL via the
 * section's "Data URL" setting), fetched once and cached across section instances.
 * The JSON is built from mybacs_Storefinder_Filialliste.xlsx and must contain only
 * rows where mybacs_gelistet = "ja". Re-export with scripts/convert-store-finder-xlsx.py.
 * loadData() also drops any payload row that is not flagged ja, in case a future
 * dump includes unlisted branches.
 *
 * Map: Leaflet + Leaflet.markercluster, self-hosted (assets/vendor-leaflet*.js) against an
 * OpenStreetMap-compatible raster tile server — no Google Maps, no API key.
 *
 * Address search: geocoded via OpenStreetMap's free Nominatim API (no key, no billing).
 * Retailer tabs filter the list and map. Distance/radius filtering is plain Haversine math.
 */
(function () {
  'use strict';

  const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
  const RETAILER_PALETTE = ['#2f6fed', '#e0483e', '#f2994a', '#1fab72', '#8956d6', '#0091ae', '#c2185b', '#5d4037'];
  const RETAILER_ORDER = ['dm', 'ROSSMANN', 'Müller', 'budni'];
  const PIN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M10 0C4.85 0 1.59 5.67 4.13 10.26l5.21 9.41a.64.64 0 0 0 1.12.01l5.36-9.34C18.45 5.76 15.2 0 10 0Zm0 10.1A3.19 3.19 0 1 1 10 3.72a3.19 3.19 0 0 1 0 6.38Z"/></svg>';

  class StoreFinder {
    static dataPromise = null;
    static dataUrlUsed = null;

    constructor(root) {
      this.root = root;
      this.dataUrl = root.dataset.dataUrl;
      this.tileUrl = root.dataset.tileUrl;
      this.attribution = root.dataset.attribution;
      this.country = root.dataset.country || '';
      this.centerLat = parseFloat(root.dataset.centerLat) || 51.1657;
      this.centerLng = parseFloat(root.dataset.centerLng) || 10.4515;
      this.defaultZoom = parseInt(root.dataset.defaultZoom, 10) || 6;
      this.radiusKm = parseFloat(root.dataset.radiusKm) || 50;
      this.maxResults = parseInt(root.dataset.maxResults, 10) || 25;
      this.strings = this.readJsonScript('[data-store-finder-strings]');

      this.formEl = root.querySelector('[data-store-finder-form]');
      this.mapEl = root.querySelector('[data-store-finder-map]');
      this.searchInput = root.querySelector('[data-store-finder-search]');
      this.searchSubmitBtn = root.querySelector('[data-store-finder-search-submit]');
      this.tabsEl = root.querySelector('[data-store-finder-tabs]');
      this.listEl = root.querySelector('[data-store-finder-list]');
      this.countEl = root.querySelector('[data-store-finder-count]');
      this.statusEl = root.querySelector('[data-store-finder-status]');

      this.stores = [];
      this.markerById = new Map();
      this.retailerColors = {};
      this.selectedRetailerValue = '';
      this.origin = null;
      this.originMarker = null;
      this.lastGeocodeAt = 0;
    }

    readJsonScript(selector) {
      const el = this.root.querySelector(selector);
      if (!el) return {};
      try {
        return JSON.parse(el.textContent);
      } catch (error) {
        console.warn('[StoreFinder] invalid strings JSON', error);
        return {};
      }
    }

    t(key, replacements) {
      let text = this.strings[key] || '';
      if (replacements) {
        Object.keys(replacements).forEach((token) => {
          text = text.replace(`{{ ${token} }}`, replacements[token]);
        });
      }
      return text;
    }

    escape(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    async init() {
      if (!this.mapEl || typeof L === 'undefined') return;

      this.initMap();
      this.bindEvents();
      this.setStatus(this.t('loading') || 'Loading stores…');

      try {
        this.stores = await this.loadData();
      } catch (error) {
        console.warn('[StoreFinder] failed to load store data', error);
        this.setStatus(this.t('errorInit') || 'Store finder could not be loaded.');
        return;
      }

      this.buildMarkers();
      this.buildRetailerTabs();
      this.applyFilters();
      this.setStatus('');
    }

    static isListed(store) {
      const flag = store && (store.listed != null ? store.listed : store.mybacs_gelistet);
      if (flag == null || flag === '') return true;
      return String(flag).trim().toLowerCase() === 'ja';
    }

    static hasCoords(store) {
      return store && Number.isFinite(Number(store.lat)) && Number.isFinite(Number(store.lng));
    }

    static normalizeStores(data) {
      const list = Array.isArray(data)
        ? data
        : data && Array.isArray(data.stores)
          ? data.stores
          : [];
      return list.filter((store) => StoreFinder.isListed(store) && StoreFinder.hasCoords(store));
    }

    loadData() {
      if (StoreFinder.dataUrlUsed !== this.dataUrl) {
        StoreFinder.dataUrlUsed = this.dataUrl;
        StoreFinder.dataPromise = null;
      }
      if (!StoreFinder.dataPromise) {
        StoreFinder.dataPromise = fetch(this.dataUrl, { credentials: 'omit' })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((data) => StoreFinder.normalizeStores(data));
      }
      return StoreFinder.dataPromise;
    }

    initMap() {
      this.map = L.map(this.mapEl, {
        minZoom: 4,
        maxZoom: 18,
        scrollWheelZoom: false,
      }).setView([this.centerLat, this.centerLng], this.defaultZoom);

      L.tileLayer(this.tileUrl, {
        attribution: this.attribution,
        maxZoom: 19,
      }).addTo(this.map);

      this.clusterGroup = L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 60,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        disableClusteringAtZoom: 16,
      });
      this.map.addLayer(this.clusterGroup);
    }

    colorForRetailer(retailer) {
      if (!this.retailerColors[retailer]) {
        const index = Object.keys(this.retailerColors).length % RETAILER_PALETTE.length;
        this.retailerColors[retailer] = RETAILER_PALETTE[index];
      }
      return this.retailerColors[retailer];
    }

    makeIcon(retailer) {
      const color = this.colorForRetailer(retailer);
      return L.divIcon({
        className: 'store-finder__marker',
        html: `<span class="store-finder__pin" style="--pin-color:${color}"></span>`,
        iconSize: [22, 22],
        iconAnchor: [11, 21],
        popupAnchor: [0, -20],
      });
    }

    makeOriginIcon() {
      return L.divIcon({
        className: 'store-finder__marker store-finder__marker--origin',
        html: '<span class="store-finder__origin-pin"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
    }

    buildMarkers() {
      this.stores.forEach((store) => {
        const marker = L.marker([store.lat, store.lng], { icon: this.makeIcon(store.retailer) });
        marker.bindPopup(this.popupHtml(store));
        marker.on('click', () => this.highlightListItem(store.id));
        this.markerById.set(store.id, marker);
      });
    }

    popupHtml(store) {
      return `
        <div class="store-finder__popup">
          <span class="store-finder__popup-retailer">${this.escape(store.retailer)}</span>
          <strong>${this.escape(store.name)}</strong>
          <p>${this.escape(store.address)}<br>${this.escape(store.zip)} ${this.escape(store.city)}</p>
          <a href="${store.mapsUrl}" target="_blank" rel="noopener">${this.escape(this.t('directions') || 'Get directions')}</a>
        </div>
      `;
    }

    renderMarkersFor(stores) {
      const markers = stores.map((store) => this.markerById.get(store.id)).filter(Boolean);
      this.clusterGroup.clearLayers();
      this.clusterGroup.addLayers(markers);
    }

    setOriginMarker(origin) {
      if (this.originMarker) {
        this.map.removeLayer(this.originMarker);
        this.originMarker = null;
      }
      if (!origin) return;
      this.originMarker = L.marker([origin.lat, origin.lng], { icon: this.makeOriginIcon(), zIndexOffset: 1000 })
        .addTo(this.map)
        .bindPopup(this.escape(origin.label));
    }

    fitToResults(stores, origin) {
      const points = stores.map((store) => [store.lat, store.lng]);
      if (origin) points.push([origin.lat, origin.lng]);
      if (!points.length) {
        this.map.setView([this.centerLat, this.centerLng], this.defaultZoom);
        return;
      }
      if (points.length === 1) {
        this.map.setView(points[0], 14);
        return;
      }
      this.map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: origin ? 13 : 7 });
    }

    buildRetailerTabs() {
      if (!this.tabsEl) return;
      const found = Array.from(new Set(this.stores.map((store) => store.retailer)));
      const retailers = found.sort((a, b) => {
        const aIndex = RETAILER_ORDER.findIndex((name) => name.toLowerCase() === a.toLowerCase());
        const bIndex = RETAILER_ORDER.findIndex((name) => name.toLowerCase() === b.toLowerCase());
        const aRank = aIndex === -1 ? RETAILER_ORDER.length : aIndex;
        const bRank = bIndex === -1 ? RETAILER_ORDER.length : bIndex;
        return aRank - bRank || a.localeCompare(b, 'de');
      });

      this.tabsEl.innerHTML = retailers
        .map(
          (retailer) => `
            <button
              type="button"
              class="store-finder__tab"
              data-retailer="${this.escape(retailer)}"
              aria-pressed="false"
            >${this.escape(retailer.toUpperCase())}</button>
          `
        )
        .join('');

      this.tabsEl.querySelectorAll('[data-retailer]').forEach((button) => {
        button.addEventListener('click', () => this.selectRetailer(button.dataset.retailer));
      });
    }

    selectRetailer(retailer) {
      this.selectedRetailerValue = this.selectedRetailerValue === retailer ? '' : retailer;
      if (this.tabsEl) {
        this.tabsEl.querySelectorAll('[data-retailer]').forEach((button) => {
          const active = button.dataset.retailer === this.selectedRetailerValue;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
      this.applyFilters();
    }

    selectedRetailer() {
      return this.selectedRetailerValue;
    }

    haversineKm(a, b) {
      const R = 6371;
      const toRad = (deg) => (deg * Math.PI) / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.asin(Math.sqrt(h));
    }

    formatDistance(km) {
      return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
    }

    async handleSearchSubmit(event) {
      event.preventDefault();
      const query = (this.searchInput.value || '').trim();
      if (!query) {
        this.origin = null;
        this.setOriginMarker(null);
        this.applyFilters();
        return;
      }

      this.setStatus(this.t('searching') || 'Searching…');
      this.setBusy(true);

      try {
        const result = await this.geocode(query);
        if (!result) {
          this.setStatus(this.t('errorGeocode') || 'Address could not be found.');
          this.setBusy(false);
          return;
        }
        this.origin = { lat: result.lat, lng: result.lng, label: query };
        this.setBusy(false);
        this.setStatus('');
        this.applyFilters();
      } catch (error) {
        console.warn('[StoreFinder] geocoding failed', error);
        this.setStatus(this.t('errorGeocode') || 'Address could not be found.');
        this.setBusy(false);
      }
    }

    async geocode(query) {
      const wait = Math.max(0, 1100 - (Date.now() - this.lastGeocodeAt));
      if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
      this.lastGeocodeAt = Date.now();

      const params = new URLSearchParams({ format: 'json', limit: '1', q: query });
      if (this.country) params.set('countrycodes', this.country);

      const response = await fetch(`${NOMINATIM_URL}?${params.toString()}`, { credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!data.length) return null;
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }

    setBusy(busy) {
      if (this.searchSubmitBtn) this.searchSubmitBtn.disabled = busy;
      if (this.searchInput) this.searchInput.disabled = busy;
    }

    filteredStores() {
      const retailer = this.selectedRetailer();
      return this.stores.filter((store) => !retailer || store.retailer === retailer);
    }

    applyFilters() {
      const retailerStores = this.filteredStores();
      let rows = retailerStores.map((store) => ({
        store,
        distanceKm: this.origin ? this.haversineKm(this.origin, store) : null,
      }));

      if (this.origin) {
        rows = rows.filter((row) => row.distanceKm <= this.radiusKm).sort((a, b) => a.distanceKm - b.distanceKm);
      } else {
        rows = rows.sort((a, b) => a.store.city.localeCompare(b.store.city, 'de') || a.store.name.localeCompare(b.store.name, 'de'));
      }

      const visibleStores = rows.map((row) => row.store);
      this.setOriginMarker(this.origin);
      this.renderMarkersFor(visibleStores);
      if (this.origin) {
        this.fitToResults(visibleStores, this.origin);
      } else {
        this.map.setView([this.centerLat, this.centerLng], this.defaultZoom);
      }
      this.renderResultsList(rows, retailerStores.length);
    }

    renderResultsList(rows, filteredCount) {
      const shown = rows.length;
      this.setCount(this.t('resultsOf', { shown, total: filteredCount }) || `${shown} / ${filteredCount}`);

      if (!rows.length) {
        this.listEl.innerHTML = `<li class="store-finder__empty">${this.escape(
          this.t('noResults', { radius: this.radiusKm }) || 'No stores found.'
        )}</li>`;
        return;
      }

      const listRows = rows.slice(0, this.maxResults);
      this.listEl.innerHTML = listRows
        .map(({ store, distanceKm }) => {
          const address = `${this.escape(store.address)} · ${this.escape(store.zip)} ${this.escape(store.city)}`;
          const distance = distanceKm != null ? `<span class="store-finder__item-distance">${this.formatDistance(distanceKm)}</span>` : '';
          return `
            <li>
              <button type="button" class="store-finder__item" data-store-id="${this.escape(store.id)}">
                <span class="store-finder__item-pin">${PIN_SVG}</span>
                <span class="store-finder__item-body">
                  <strong>${this.escape(store.name)}</strong>
                  <span>${address}</span>
                </span>
                ${distance}
              </button>
            </li>
          `;
        })
        .join('');

      this.listEl.querySelectorAll('[data-store-id]').forEach((button) => {
        button.addEventListener('click', () => this.focusStore(button.dataset.storeId));
      });
    }

    focusStore(storeId) {
      const marker = this.markerById.get(storeId);
      if (!marker) return;
      this.clusterGroup.zoomToShowLayer(marker, () => {
        marker.openPopup();
      });
      this.highlightListItem(storeId);
    }

    highlightListItem(storeId) {
      if (!this.listEl) return;
      this.listEl.querySelectorAll('.store-finder__item').forEach((item) => {
        item.classList.toggle('is-active', item.dataset.storeId === storeId);
      });
      const active = this.listEl.querySelector('.store-finder__item.is-active');
      if (active) active.scrollIntoView({ block: 'nearest' });
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text;
    }

    setCount(text) {
      if (this.countEl) this.countEl.textContent = text;
    }

    bindEvents() {
      if (this.formEl) {
        this.formEl.addEventListener('submit', (event) => this.handleSearchSubmit(event));
      }
      if (this.searchInput) {
        this.searchInput.addEventListener('search', () => {
          if (!(this.searchInput.value || '').trim()) {
            this.origin = null;
            this.setOriginMarker(null);
            this.applyFilters();
          }
        });
      }
    }
  }

  function initStoreFinders(scope) {
    (scope || document).querySelectorAll('[data-store-finder]').forEach((root) => {
      if (root.dataset.storeFinderInitialized) return;
      root.dataset.storeFinderInitialized = 'true';
      new StoreFinder(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initStoreFinders());
  } else {
    initStoreFinders();
  }

  document.addEventListener('shopify:section:load', (event) => initStoreFinders(event.target));
})();
